/*
 * Shared Sweeps in the UpEZ side cart: per-line entries badges, badges on UpEZ's
 * upsell products, a total entries row above checkout, and the three-tier bonus
 * entries message.
 *
 * The side cart is the UpEZ app (<upnova-widget id="upezCart">), rendered in an
 * open shadow root, so the theme's cart-drawer.liquid never shows. Everything
 * here is inserted into that shadow root and put back whenever UpEZ re-renders.
 *
 * Entries need product metafields, which /cart.js doesn't include, so line
 * badges and totals come from sections/sweeps-cart-data.liquid, refetched
 * whenever the cart changes. Upsell badges come from
 * sections/sweeps-product-badge.liquid (once per product), with the count
 * recomputed for the price UpEZ displays.
 *
 * Config comes from theme settings via window.sweepsCartConfig
 * (snippets/global-js.liquid). Tiers are not cumulative: the message targets the
 * next tier, and the highest tier reached adds its bonus to the total.
 */
(function () {
  const config = window.sweepsCartConfig;
  if (!config || window.SweepsCart) return;

  const tiers = (config.tiers || []).slice().sort((a, b) => a.spend - b.spend);
  const SECTION_ID = 'sweeps-cart-data';
  const TIERS_ANCHOR = '.upez__shop_progress_pristine';
  const HEADER_SELECTOR = '.sticky.top-0';
  const FOOTER_SELECTOR = '.sticky.bottom-0';
  const LINE_ROW_SELECTOR = '[data-product-handle][data-line-price]';
  const PRODUCT_TITLE_SELECTOR = '.upnova-product-title';
  const PRODUCT_BADGE_SECTION = 'sweeps-product-badge';
  const CART_URL_PATTERN = /\/cart(?:\/(add|change|update|clear))?\.js(?:on)?$/;

  let data = null;
  let cartSignature = null;
  let refreshTimer = null;
  let placeScheduled = false;
  const productBadges = new Map();
  let badgeStyles = '';
  const tiersElement = createElement('ss-tiers');
  const totalElement = createElement('ss-cart-total');

  const TICKET_ICON =
    '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M3 8a2 2 0 0 0 2-2h14a2 2 0 0 0 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 0-2 2H5a2 2 0 0 0-2-2v-2a2 2 0 0 0 0-4V8z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M14 7v10" stroke="currentColor" stroke-width="1.6" stroke-dasharray="1.5 2"/></svg>';

  const STYLES = `
    .ss-tiers, .ss-cart-total { --ss-brand: #0F4C81; font-family: inherit; }
    .ss-tiers { display: flex; align-items: center; justify-content: center; gap: 6px; margin: 0; padding: 8px 16px 10px; font-size: 13px; font-weight: 500; line-height: 1.3; text-align: center; color: var(--ss-brand); }
    .ss-tiers svg { flex: 0 0 auto; }
    .ss-tiers strong { font-weight: 700; }
    .ss-cart-total { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; padding: 12px 16px 0; font-size: 14px; font-weight: 600; color: var(--ss-brand); }
    .ss-cart-total__note { font-size: 12px; font-weight: 500; opacity: .8; }
    .ss-line-entries { margin-top: 4px; }
    .ss-line-entries .ss-banner-wrapper--card-min { margin-left: 0; }
    .ss-line-entries .ss-banner-wrapper--card-min .ss-banner-multiplier { align-self: stretch; display: flex; align-items: center; }
    .ss-line-entries .ss-banner-count, .ss-upsell-entries .ss-banner-count { padding: 3px 0; }
    .ss-upsell-entries { margin-top: 2px; min-width: 0; }
    .ss-upsell-entries .ss-banner-wrapper--card-min { margin-left: 0; }
    .ss-upsell-entries .ss-banner-wrapper--card-min .ss-banner-count { margin-left: 6px; font-size: 11px; }
    .ss-upsell-entries .ss-banner-wrapper--card-min .ss-banner-multiplier { align-self: stretch; display: flex; align-items: center; padding: 0 6px; font-size: 11px; }
  `;

  function createElement(className) {
    const element = document.createElement('div');
    element.className = className;
    element.hidden = true;
    return element;
  }

  function getRoot() {
    const host = document.getElementById('upezCart');
    return host && host.shadowRoot;
  }

  function formatMoney(cents, currency) {
    const amount = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function formatEntries(entries) {
    return Number(entries).toLocaleString('en-US');
  }

  // Tier spend is in the store currency; convert for multi-currency carts.
  function thresholdCents(tier) {
    const rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    return Math.round(tier.spend * 100 * rate);
  }

  function reachedTierIndex() {
    let reached = -1;
    tiers.forEach((tier, i) => {
      if (data.totalPrice >= thresholdCents(tier)) reached = i;
    });
    return reached;
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // One line of text under UpEZ's free shipping bar, e.g. "Spend $40.10 more to earn +150 bonus entries".
  function renderTiers() {
    if (!tiers.length || !data || !data.itemCount) {
      tiersElement.hidden = true;
      return;
    }

    const total = data.totalPrice;
    const thresholds = tiers.map(thresholdCents);
    const nextIndex = thresholds.findIndex((cents) => total < cents);
    const complete = nextIndex === -1;
    const entries = '+' + formatEntries(tiers[complete ? tiers.length - 1 : nextIndex].entries);
    const template = complete ? config.textComplete : config.textProgress;
    const amount = complete ? '' : formatMoney(thresholds[nextIndex] - total, data.currency);

    tiersElement.hidden = false;
    tiersElement.setAttribute('role', 'status');
    tiersElement.innerHTML =
      TICKET_ICON +
      '<span>' +
      escapeHtml(template)
        .replace('[amount]', `<strong>${escapeHtml(amount)}</strong>`)
        .replace('[entries]', `<strong>${escapeHtml(entries)}</strong>`) +
      '</span>';
  }

  function renderTotal() {
    if (!config.showEntries || !data || !data.itemCount) {
      totalElement.hidden = true;
      return;
    }
    const reached = tiers.length ? reachedTierIndex() : -1;
    const tierBonus = reached === -1 ? 0 : tiers[reached].entries;
    const total = data.totalEntries + tierBonus;
    if (!total) {
      totalElement.hidden = true;
      return;
    }

    totalElement.hidden = false;
    totalElement.setAttribute('role', 'status');
    totalElement.innerHTML =
      `<span>${config.totalLabel}` +
      (tierBonus ? ` <span class="ss-cart-total__note">(incl. +${formatEntries(tierBonus)} bonus)</span>` : '') +
      `</span><span>${formatEntries(total)}</span>`;
  }

  function ensureStyles(root) {
    let style = root.getElementById('ss-cart-styles');
    if (!style) {
      style = document.createElement('style');
      style.id = 'ss-cart-styles';
      root.appendChild(style);
    }
    const css = STYLES + badgeStyles;
    if (style.textContent !== css) style.textContent = css;
  }

  // Match UpEZ rows to cart lines by handle, preferring the same line price, so two
  // lines of one product (different variants) each get their own badge.
  function placeLineBadges(root) {
    const rows = root.querySelectorAll(LINE_ROW_SELECTOR);
    const lines = config.showEntries && data ? data.lines.slice() : [];

    rows.forEach((row) => {
      const candidates = lines.filter((line) => line.handle === row.dataset.productHandle);
      const line = candidates.find((l) => l.linePrice === row.dataset.linePrice) || candidates[0];
      if (line) lines.splice(lines.indexOf(line), 1);

      const info = row.children[1] || row;
      const existing = row.querySelector('.ss-line-entries');
      if (!line) {
        if (existing) existing.remove();
        return;
      }
      if (existing && existing.dataset.key === line.key && existing.dataset.version === String(data.version)) return;
      if (existing) existing.remove();

      const badge = document.createElement('div');
      badge.className = 'ss-line-entries';
      badge.dataset.key = line.key;
      badge.dataset.version = String(data.version);
      badge.innerHTML = line.html;
      info.appendChild(badge);
    });
  }

  function place() {
    const root = getRoot();
    if (!root) return;
    ensureStyles(root);

    const anchor = root.querySelector(TIERS_ANCHOR);
    if (anchor) {
      if (anchor.nextElementSibling !== tiersElement) anchor.after(tiersElement);
    } else {
      const header = root.querySelector(HEADER_SELECTOR);
      if (header && tiersElement.parentNode !== header) header.appendChild(tiersElement);
    }

    const footer = root.querySelector(FOOTER_SELECTOR);
    if (footer && footer.firstElementChild !== totalElement) footer.prepend(totalElement);

    placeLineBadges(root);
    placeUpsellBadges(root);
  }

  function schedulePlace() {
    if (placeScheduled) return;
    placeScheduled = true;
    requestAnimationFrame(() => {
      placeScheduled = false;
      place();
    });
  }

  function routesRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  // Badge HTML per product handle; '' when the product isn't eligible.
  function loadProductBadge(handle) {
    if (!productBadges.has(handle)) {
      const request = fetch(`${routesRoot()}products/${encodeURIComponent(handle)}?sections=${PRODUCT_BADGE_SECTION}`)
        .then((response) => (response.ok ? response.json() : {}))
        .then((sections) => {
          const doc = new DOMParser().parseFromString(sections[PRODUCT_BADGE_SECTION] || '', 'text/html');
          const container = doc.getElementById(PRODUCT_BADGE_SECTION);
          const style = container && container.querySelector('style');
          if (style && !badgeStyles) badgeStyles = style.textContent;
          if (container) container.querySelectorAll('style').forEach((el) => el.remove());
          productBadges.set(handle, container ? container.innerHTML.trim() : '');
          schedulePlace();
        })
        .catch(() => productBadges.set(handle, ''));
      productBadges.set(handle, request);
    }
    const badge = productBadges.get(handle);
    return typeof badge === 'string' ? badge : null;
  }

  function parseMoneyCents(text) {
    const digits = text.replace(/[^\d.,]/g, '');
    if (!digits) return 0;
    const normalized = digits.replace(/[.,](?=\d{3}(?:\D|$))/g, '').replace(',', '.');
    return Math.round(parseFloat(normalized) * 100) || 0;
  }

  // UpEZ product cards have no data attributes, so find the price from the title:
  // walk up to the first ancestor that shows a price, then take the first price
  // that isn't struck through (the compare-at price is).
  function findPrice(title) {
    let card = title;
    for (let i = 0; i < 5 && card.parentElement && !/\d/.test(card.textContent.replace(title.textContent, '')); i++) {
      card = card.parentElement;
    }
    return [...card.querySelectorAll('*')].find(
      (el) =>
        !el.children.length &&
        el !== title &&
        /^\D{0,4}\s?\d[\d.,\s]*\D{0,4}$/.test(el.textContent.trim()) &&
        getComputedStyle(el).textDecorationLine !== 'line-through'
    );
  }

  function placeUpsellBadges(root) {
    root.querySelectorAll(PRODUCT_TITLE_SELECTOR).forEach((title) => {
      if (title.closest(LINE_ROW_SELECTOR)) return;
      const href = title.getAttribute('href') || '';
      const handle = (href.split('?')[0].match(/\/products\/([^/]+)/) || [])[1];
      const price = handle && findPrice(title);
      if (!price) return;

      // Below the price row; in a horizontal row (price + button) go below the whole row.
      const priceRow = price.parentElement;
      const container = priceRow.parentElement;
      const target = getComputedStyle(container).flexDirection === 'row' ? container : priceRow;
      const existing = target.nextElementSibling && target.nextElementSibling.classList.contains('ss-upsell-entries')
        ? target.nextElementSibling
        : null;

      const cents = parseMoneyCents(price.textContent);
      const html = config.showEntries && cents > 0 ? loadProductBadge(handle) : '';
      if (!html) {
        if (existing) existing.remove();
        return;
      }
      if (existing && existing.dataset.handle === handle && existing.dataset.price === String(cents)) return;

      const badge = existing || document.createElement('div');
      badge.className = 'ss-upsell-entries';
      badge.dataset.handle = handle;
      badge.dataset.price = String(cents);
      badge.innerHTML = html;
      if (window.SharedSweeps) SharedSweeps.update(badge, cents);
      // Carousel cards are ~102px wide: "629 Entries" fits where "Earn 629 Entries" clips.
      // Cart line badges keep the full wording — they have the room.
      const lead = badge.querySelector('.ss-banner-lead');
      if (lead) lead.remove();
      if (!existing) target.after(badge);
    });
  }

  function parseSection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const container = doc.getElementById(SECTION_ID);
    if (!container) return null;

    // shared-sweeps.liquid inlines its <style> per badge; keep one copy for the shadow root.
    const firstStyle = container.querySelector('style');
    if (firstStyle && !badgeStyles) badgeStyles = firstStyle.textContent;
    container.querySelectorAll('style').forEach((style) => style.remove());

    return {
      version: (data ? data.version : 0) + 1,
      totalPrice: parseInt(container.dataset.totalPrice, 10) || 0,
      itemCount: parseInt(container.dataset.itemCount, 10) || 0,
      currency: container.dataset.currency,
      totalEntries: parseInt(container.querySelector('[data-total-entries]').dataset.totalEntries, 10) || 0,
      lines: [...container.querySelectorAll('[data-line]')].map((line) => ({
        key: line.dataset.key,
        handle: line.dataset.handle,
        linePrice: line.dataset.linePrice,
        html: line.innerHTML,
      })),
    };
  }

  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      fetch(`${routesRoot()}cart?sections=${SECTION_ID}`)
        .then((response) => response.json())
        .then((sections) => {
          const next = parseSection(sections[SECTION_ID] || '');
          if (!next) return;
          data = next;
          renderTiers();
          renderTotal();
          place();
        })
        .catch(() => {});
    }, 150);
  }

  // UpEZ reads /cart.js a lot; only refetch entries when the cart actually changed.
  function handleCartJson(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const signature =
      cart.total_price + '|' + cart.items.map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`).join(',');
    if (signature === cartSignature) return;
    cartSignature = signature;
    refresh();
  }

  function handleCartResponse(url, readJson) {
    let path;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch (e) {
      return;
    }
    const match = path.match(CART_URL_PATTERN);
    if (!match) return;
    // add.js returns only the added items, not the cart.
    if (match[1] === 'add') {
      cartSignature = null;
      refresh();
      return;
    }
    readJson().then(handleCartJson, () => {});
  }

  const originalFetch = window.fetch;
  window.fetch = function (input) {
    const url = typeof input === 'string' ? input : input && input.url;
    return originalFetch.apply(this, arguments).then((response) => {
      if (url && response.ok) handleCartResponse(url, () => response.clone().json());
      return response;
    });
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) {
        handleCartResponse(String(url), () => Promise.resolve(JSON.parse(this.responseText)));
      }
    });
    return originalOpen.apply(this, arguments);
  };

  // UpEZ re-renders its shadow DOM on every cart change; keep our elements in place.
  function watchUpez() {
    const root = getRoot();
    if (!root) return false;
    new MutationObserver(schedulePlace).observe(root, { childList: true, subtree: true });
    place();
    return true;
  }

  // UpEZ loads async and attaches its shadow root late, which no DOM observer sees.
  if (!watchUpez()) {
    let attempts = 0;
    const poll = setInterval(() => {
      if (watchUpez() || ++attempts > 60) clearInterval(poll);
    }, 500);
  }

  refresh();

  window.SweepsCart = { refresh };
})();
